import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import { AutotextService } from "../autotext.service";
import { ExamsService } from "../exams.service";
import { PatientsService } from "../patients.service";
import { CKEditorComponent } from "@ckeditor/ckeditor5-angular";
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Alignment,
  FontSize,
  FontFamily,
  FontColor,
  FontBackgroundColor,
  List,
  Link,
  Table,
  TableToolbar,
  TableProperties,
  TableCellProperties,
  TableColumnResize,
} from "ckeditor5";
import { Observable, Subscription } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { LoginService } from "../login.service";
import { CommerrorService } from "../commerror.service";

export interface Template {
  TemplateID: string;
  UID: string;
  Title: string;
  TemplateContent: string;
}

@Component({
    selector: "app-consultation",
    templateUrl: "./consultation.component.html",
    styleUrls: ["./consultation.component.scss"],
    standalone: false
})
export class ConsultationComponent implements OnInit, OnDestroy {
  private prefillSub: Subscription;
  @ViewChild("Title") Title: ElementRef;
  @ViewChild("titleValue") titleValue: ElementRef;
  @ViewChild("ckeditorRef") ckeditorRef: CKEditorComponent;
  myAutoTextLists: any[] = [];
  showAutotextPanel = false;
  showEdit = false;
  showPopup = false;
  showDeleteConfirm = false;
  templateToDelete: Template = null;
  inputValue = "";
  templates: Template[] = [];
  selectedTemplate = false;
  consultation: UntypedFormGroup;
  input1: string;
  input2: string;
  public FormContentEditor = ClassicEditor;
  public formContentEditorConfig = {
    licenseKey: "GPL",
    language: {
      ui: "en",
      content: "he",
    },
    plugins: [
      Essentials,
      Paragraph,
      Bold,
      Italic,
      Underline,
      Alignment,
      FontSize,
      FontFamily,
      FontColor,
      FontBackgroundColor,
      List,
      Link,
      Table,
      TableToolbar,
      TableProperties,
      TableCellProperties,
      TableColumnResize,
    ],
    toolbar: [
      "undo",
      "redo",
      "|",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "bold",
      "italic",
      "underline",
      "|",
      "alignment",
      "|",
      "bulletedList",
      "numberedList",
      "|",
      "link",
      "insertTable",
    ],
    table: {
      contentToolbar: [
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableProperties",
        "tableCellProperties",
      ],
    },
  };
  constructor(
    private patService: PatientsService,
    private http: HttpClient,
    public atS: AutotextService,
    public es: ExamsService,
    private fb: UntypedFormBuilder,
    public ls: LoginService,
    private ce: CommerrorService
  ) {}

  ngOnInit() {
    this.consultation = this.fb.group({
      FormContent: [""],
      selectedTemplate: [""],
      Title: new UntypedFormControl("", Validators.minLength(2)),
    });
    this.es.fillForm(this.consultation, "Forms", false, true);
    if(!this.ls.ishospital){
    this.http.get("/api/templates").subscribe((data) => {
      this.templates = data as Template[];
    });}
    this.applyPendingClinicTemplate();
    this.loadClinicsAutotext();
    // the doctor pulled the patient's earlier clinic exam: bring its letter body in
    this.prefillSub = this.es.clinicPrefilled$.subscribe((fromExamID) => this.pullLetterFrom(fromExamID));
  }

  ngOnDestroy() {
    if (this.prefillSub) this.prefillSub.unsubscribe();
  }

  /**
   * A canvas (letter) clinic exam keeps its body in Forms, not in the tables the
   * server copies, so it goes through the normal form save path instead.
   */
  private pullLetterFrom(fromExamID: number) {
    if (!this.isClinicsExam || this.es.isClosed || !fromExamID) return;
    try {
      this.http.get("/api/get", { params: { table: "Forms", ExamID: String(fromExamID) } }).subscribe(
        (data: any) => {
          if (data && !data.hasOwnProperty("empty") && data["FormContent"]) {
            this.consultation.get("FormContent").setValue(data["FormContent"]);
          }
        },
        (err) => { console.log(err); }
      );
    } catch (e) {
      console.log(e);
    }
  }

  get isClinicsExam(): boolean {
    return this.es.ExamType === "Clinics";
  }

  private applyPendingClinicTemplate() {
    if (!this.es.pendingClinicTemplate) return;
    const sub: Subscription = this.es.formLoaded$.subscribe(() => {
      const tpl = this.es.pendingClinicTemplate;
      if (!tpl) { sub.unsubscribe(); return; }
      this.consultation.get("FormContent").setValue(tpl.TemplateContent || "");
      if (tpl.Title) {
        this.consultation.get("selectedTemplate").setValue(tpl.Title);
        this.consultation.get("Title").setValue(tpl.Title);
        this.es.changeTitle(tpl.Title, true);
      }
      this.es.pendingClinicTemplate = null;
      sub.unsubscribe();
    });
  }

  private loadClinicsAutotext() {
    if (!this.isClinicsExam) return;
    const clinicTemplateId = this.es.pendingClinicTemplate?.ClinicTemplateID || (this.es.examObject as any)?.ClinicTemplateID;
    if (!clinicTemplateId) return;
    this.es.getMyAutoTextLists(clinicTemplateId).subscribe(
      (data: any) => { this.myAutoTextLists = data || []; },
      (err) => { console.log(err); }
    );
  }

  insertAutotextSnippet(content: string) {
    const editor = this.ckeditorRef && this.ckeditorRef.editorInstance;
    if (!editor) return;
    const html = (content || "").replace(/\n/g, "<br>");
    const viewFragment = editor.data.processor.toView(html);
    const modelFragment = editor.data.toModel(viewFragment);
    editor.model.change(() => {
      editor.model.insertContent(modelFragment);
    });
    this.showAutotextPanel = false;
  }
  createTemplate(titleValue: string) {
    const TemplateContent = this.consultation.get("FormContent").value;
    const Title = titleValue;

    if (!TemplateContent || !Title) {
      return;
    }

    const body = { TemplateContent, Title };
    this.http.post("/api/templates", body).subscribe((data) => {
      this.templates = data as Template[];
      this.titleValue.nativeElement.value = "";
    });
  }

  checkTemplate(titleValue: string) {
    if (!titleValue || !this.consultation.get("FormContent").value) {
      return true;
    }
    return false;
  }

  confirmDeleteTemplate(templateId: string) {
    if (!templateId) return;
    const [template] = this.templates.filter(
      (t) => t.TemplateID == templateId
    );
    this.templateToDelete = template || null;
    this.showDeleteConfirm = true;
  }
  deleteTemplate() {
    if (!this.templateToDelete) return;
    this.http.delete(`/api/templates/${this.templateToDelete.TemplateID}`).subscribe((data) => {
      this.templates = data as Template[];
      this.templateToDelete = null;
      this.showDeleteConfirm = false;
    });
  }
  editTemplate(templateId: string) {
    if (!templateId) return;
    const TemplateContent = this.consultation.get("FormContent").value;
    const [selectedTemplate] = this.templates.filter(
      (template) => template.TemplateID == templateId
    );
    const currentTitle = selectedTemplate.Title;
    const newTitle = this.titleValue.nativeElement.value;
    const Title = newTitle || currentTitle;
    const body = { Title, TemplateContent };
    this.http.put(`/api/templates/${templateId}`, body).subscribe((data) => {
      this.templates = data as Template[];
      this.titleValue.nativeElement.value = "";
      this.showPopup = false;
    });
  }
  setTemplate(templateId: string) {
    if (!templateId) return;
    this.selectedTemplate = true;
    let [selectedTemplate] = this.templates.filter(
      (template) => template.TemplateID == templateId
    );
    const formContent=this.consultation
    .get("FormContent").value;
    this.consultation
      .get("FormContent")
      .setValue(formContent? formContent+ '<br>' +selectedTemplate.TemplateContent:selectedTemplate.TemplateContent);
      let title=selectedTemplate.Title
      this.consultation.get('selectedTemplate').setValue(title)

      if(!this.consultation.get('Title').value){
        this.consultation.get('Title').setValue(title);
        this.es.changeTitle(this.consultation.get('Title').value,true)
       }

      else if(this.templates.filter(
        (template) => template.Title === this.consultation.get('Title').value
      ).length){
        this.consultation.get('Title').setValue(title)
        this.es.changeTitle(this.consultation.get('Title').value,true)
      }

  }
  checkTextChanged(templateId: string, title?: string) {
    if (templateId) {
      const [currentTemplate] = this.templates.filter(
        (template) => templateId == template.TemplateID
      );
      if (
        currentTemplate.TemplateContent !==
        this.consultation.get("FormContent").value
      ) {
        return (this.showEdit = true);
      }
      if (currentTemplate.Title != title && title) {
        return (this.showEdit = true);
      }
    }
    this.showEdit = false;
  }

  changeTitle(){
    if(!this.consultation.get('Title').value){
      const templateTitle=this.consultation.get('selectedTemplate').value
      this.consultation.get('Title').setValue(templateTitle)
    }
    
      this.es.changeTitle(this.consultation.get('Title').value,true)
    
  }

  loadPreviousMedicalInfo() {
    const currentExamType = this.es.ExamType;
    const patientId = this.patService.getPat().PID;
    const currentExamId = this.es.examObject?.AutoIndex;

    // Get all exams for this patient
    this.http.get(`/api/forms/${patientId}`).subscribe((data: any) => {
      // Define consultation exam types that should be grouped together
      const consultationTypes = ['Consultation Letter', 'consultation and follow up with media'];
      const isCurrentConsultation = consultationTypes.includes(currentExamType);

      // Filter exams of the same type, excluding current exam
      const previousExams = data.filter((exam: any) => {
        // If current is consultation type, match any consultation type
        const matchesType = isCurrentConsultation 
          ? consultationTypes.includes(exam.ExamType)
          : exam.ExamType === currentExamType;
        
        return matchesType && 
          exam.AutoIndex !== currentExamId &&
          exam.Forms?.FormContent;
      });

      if (previousExams.length === 0) {
        this.ce.errorGastro('No Previous Information', 'No previous medical information found.');
        return;
      }

      // Sort by date (newest first)
      previousExams.sort((a: any, b: any) => 
        new Date(b.CreationTime).getTime() - new Date(a.CreationTime).getTime()
      );

      // Build HTML content with dates
      let compiledContent = '';
      previousExams.forEach((exam: any, index: number) => {
        const examDate = new Date(exam.CreationTime).toLocaleDateString('en-GB');
        compiledContent += `<h3>${examDate}</h3>`;
        compiledContent += exam.Forms.FormContent;
        if (index < previousExams.length - 1) {
          compiledContent += '<br><hr><br>';
        }
      });

      // Insert into editor
      const currentContent = this.consultation.get('FormContent').value || '';
      const newContent = currentContent ? 
        currentContent + '<br><hr><br>' + compiledContent : 
        compiledContent;
      this.consultation.get('FormContent').setValue(newContent);
    }, (error) => {
      console.error('Error loading previous medical information:', error);
      this.ce.errorGastro('Error Loading Information', 'Error loading previous medical information.');
    });
  }

  loadLastMedicalInfo() {
    const currentExamType = this.es.ExamType;
    const patientId = this.patService.getPat().PID;
    const currentExamId = this.es.examObject?.AutoIndex;

    // Get all exams for this patient
    this.http.get(`/api/forms/${patientId}`).subscribe((data: any) => {
      // Define consultation exam types that should be grouped together
      const consultationTypes = ['Consultation Letter', 'consultation and follow up with media'];
      const isCurrentConsultation = consultationTypes.includes(currentExamType);

      // Filter exams of the same type, excluding current exam
      const previousExams = data.filter((exam: any) => {
        // If current is consultation type, match any consultation type
        const matchesType = isCurrentConsultation 
          ? consultationTypes.includes(exam.ExamType)
          : exam.ExamType === currentExamType;
        
        return matchesType && 
          exam.AutoIndex !== currentExamId &&
          exam.Forms?.FormContent;
      });

      if (previousExams.length === 0) {
        this.ce.errorGastro('No Previous Information', 'No previous medical information found.');
        return;
      }

      // Sort by date (newest first) and get only the most recent one
      previousExams.sort((a: any, b: any) => 
        new Date(b.CreationTime).getTime() - new Date(a.CreationTime).getTime()
      );

      const lastExam = previousExams[0];
      const examDate = new Date(lastExam.CreationTime).toLocaleDateString('en-GB');
      const compiledContent = `<h3>${examDate}</h3>` + lastExam.Forms.FormContent;

      // Insert into editor
      const currentContent = this.consultation.get('FormContent').value || '';
      const newContent = currentContent ? 
        currentContent + '<br><hr><br>' + compiledContent : 
        compiledContent;
      this.consultation.get('FormContent').setValue(newContent);
    }, (error) => {
      console.error('Error loading last medical information:', error);
      this.ce.errorGastro('Error Loading Information', 'Error loading last medical information.');
    });
  }
}
